import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { deleteEmptyKeys } from "utils";
import { useApplicantListControls, useApplicants } from "hooks";
import { ApplicantBoard, ApplicantViewLayout } from "components/Missions/Applicants";
import { Grid } from "components/Containers";
import { Banner } from "components/Banner";
import { BANNER_TYPE } from "constants/index";

/**
 * ApplicantBoardLayout
 *
 * @param {Object}        initialProgressMenu     The initial progress menu state.
 * @param {Object}        initialTab              The initial tab state.
 * @param {boolean}       hideSorts               Whether to hide sorting options.
 * @param {boolean}       hideKeywordSearch       Whether to hide keyword search.
 * @param {boolean}       hideMemberFilter        Whether to hide member filter.
 * @param {boolean}       hideAdvancedFilters     Whether to hide advanced filters.
 * @param {boolean}       showBanner              Whether to show the closed role banner.
 * @param {Object}        defaultSelected         The default selected filters.
 * @param {Object}        filtersProp             Additional filters to apply.
 * @param {Object}        variablesProp           Additional variables for fetching applicants.
 */
const ApplicantBoardLayout = ({
  initialProgressMenu,
  initialTab,
  hideSorts,
  hideKeywordSearch,
  hideMemberFilter,
  hideAdvancedFilters,
  showBanner,
  defaultSelected,
  filtersProp,
  variablesProp,
  ...props
}) => {
  const { selected, setSelected, displaySelected, sortingOptions, hasKeywordSearch, resolvedSort } =
    useApplicantListControls({ defaultSelected, initialProgressMenu, initialTab });

  const { filterMetadata, loading } = useApplicants({
    ...deleteEmptyKeys(selected),
    sortKey: resolvedSort.sortKey,
    sortDirection: resolvedSort.sortDirection,
    filters: filtersProp,
    variables: variablesProp,
  });

  return (
    <ApplicantViewLayout
      hideSorts={hideSorts}
      hideKeywordSearch={hideKeywordSearch}
      hideMemberFilter={hideMemberFilter}
      hideAdvancedFilters={hideAdvancedFilters}
      selected={selected}
      setSelected={setSelected}
      displaySelected={displaySelected}
      sortingOptions={sortingOptions}
      hasKeywordSearch={hasKeywordSearch}
      filterMetadata={filterMetadata}
      loading={loading}
      {...props}
    >
      {showBanner && (
        <Grid.col start={1} end={13}>
          <Banner isClosable={false} type={BANNER_TYPE.warning}>
            <FormattedMessage id="Missions.Roles.Applications.BoardView.ClosedRole.BannerText" />
          </Banner>
        </Grid.col>
      )}
      <Grid.col start={1} end={13}>
        <ApplicantBoard selected={selected} resolvedSort={resolvedSort} variablesProp={variablesProp} {...props} />
      </Grid.col>
    </ApplicantViewLayout>
  );
};

ApplicantBoardLayout.defaultProps = {
  initialTab: 0,
  initialProgressMenu: 1,
  hideSorts: true,
  hideKeywordSearch: true,
  hideMemberFilter: true,
  hideAdvancedFilters: true,
};

ApplicantBoardLayout.propTypes = {
  initialTab: PropTypes.number,
  initialProgressMenu: PropTypes.number,
  hideSorts: PropTypes.bool,
  hideKeywordSearch: PropTypes.bool,
  hideMemberFilter: PropTypes.bool,
  hideAdvancedFilters: PropTypes.bool,
  showBanner: PropTypes.bool,
  defaultSelected: PropTypes.object,
  filtersProp: PropTypes.object,
  variablesProp: PropTypes.object,
};

export default ApplicantBoardLayout;
